import { useMemo } from 'react';

import { getUniversalCookie } from '@/cookies';
import { useSubscriptionType } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { useGetApiV1ReferralPopupsPartnerKey } from '@/open-api/referral-popups/referral-popups';

import { mapReferralPopupContent, shouldShowCreatorPopup } from './variant';

import type { ReferredVisitorPopupContent } from './types';

const PARTNER_KEY_PATTERN = /^[a-zA-Z0-9_-]+$/;

// PartnerStack links carry the partner key base64-encoded
const decodePartnerKey = (value: string): string => {
  try {
    const decoded = window.atob(value);
    return PARTNER_KEY_PATTERN.test(decoded) ? decoded : value;
  } catch {
    return value;
  }
};

export interface ReferredVisitorPopupState {
  content: ReferredVisitorPopupContent | null;
  /**
   * True while we can't yet tell which pop-up this visitor should see. The
   * coupon pop-up must wait, otherwise a VIP visitor could briefly get it.
   */
  isResolving: boolean;
  partnerKey: string | null;
  /** Personalised content exists and the visitor is eligible for it */
  showCreatorPopup: boolean;
}

export const useReferredVisitorPopup = (): ReferredVisitorPopupState => {
  const partnerKey = useMemo(() => {
    const cookieValue = getUniversalCookie<string>('ps_partner_key');
    return cookieValue ? decodePartnerKey(cookieValue) : null;
  }, []);

  const { settings } = useSettings();
  const subscriptionType = useSubscriptionType();
  const isEligible = !!settings && subscriptionType === 'Free';

  const { data, isPending } = useGetApiV1ReferralPopupsPartnerKey(partnerKey, {
    query: {
      enabled: !!partnerKey,
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
      refetchOnWindowFocus: false,
    },
  });

  const content = useMemo(() => mapReferralPopupContent(data), [data]);

  const isFetching = !!partnerKey && isPending;
  const isAwaitingSettings = content !== null && !settings;

  return {
    content,
    isResolving: isFetching || isAwaitingSettings,
    partnerKey,
    showCreatorPopup: shouldShowCreatorPopup({ content, isEligible }),
  };
};
